<template>
	<div class="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
		<!-- Mobile Header -->
		<header
			class="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-40"
		>
			<div class="px-4 py-4 bg-primary">
				<div class="flex items-center justify-between">
					<div class="flex items-center space-x-3">
						<router-link
							:to="{ name: 'Home' }"
							class="text-white hover:text-blue-100 transition-colors duration-200 p-1 -ml-1"
						>
							<svg
								class="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
						</router-link>
						<div class="min-w-0 flex-1">
							<h1 class="text-lg font-bold text-white truncate">
								Motor Insurance
							</h1>
							<p class="text-blue-100 text-xs">
								Step {{ currentStepIndex + 1 }} of
								{{ steps.length }}
							</p>
						</div>
					</div>
					<div
						class="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1 flex-shrink-0 ml-3"
					>
						<img
							src="/assets/Logo/nexus.svg"
							alt="Nexus Company Logo"
							class="w-full h-full object-contain"
						/>
					</div>
				</div>
			</div>

			<div class="px-4 py-4 bg-white">
				<div class="flex items-center justify-between relative">
					<div
						class="absolute top-1/2 left-8 right-8 h-0.5 bg-gray-200 -translate-y-1/2"
					></div>
					<div
						class="absolute top-1/2 left-8 h-0.5 bg-secondary -translate-y-1/2 transition-all duration-500"
						:style="{
							width:
								'calc(' +
								(currentStepIndex / (steps.length - 1)) * 100 +
								'% - 2rem)',
						}"
					></div>

					<div
						v-for="(step, index) in steps"
						:key="step.name"
						class="relative z-10 flex flex-col items-center"
					>
						<div
							:class="[
								'flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 bg-white mb-2',
								isStepActive(step.name)
									? 'border-primary text-primary shadow-sm'
									: isStepCompleted(step.name)
										? 'border-secondary text-secondary'
										: 'border-gray-300 text-gray-400',
							]"
						>
							<template
								v-if="
									isStepCompleted(step.name) &&
									!isStepActive(step.name)
								"
							>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="3"
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</template>
							<template v-else>
								<span class="text-xs font-bold">{{
									index + 1
								}}</span>
							</template>
						</div>

						<div class="text-center max-w-20">
							<p
								:class="[
									'text-xs font-medium truncate leading-tight',
									isStepActive(step.name)
										? 'text-primary'
										: isStepCompleted(step.name)
											? 'text-secondary'
											: 'text-gray-500',
								]"
							>
								{{ step.shortLabel || step.label }}
							</p>
						</div>
					</div>
				</div>
			</div>
		</header>

		<!-- Desktop Sidebar -->
		<aside
			class="hidden lg:flex lg:w-80 bg-white shadow-xl flex-col border-r border-gray-200 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto"
		>
			<div class="bg-primary px-6 py-6">
				<div class="flex items-center justify-between">
					<h1 class="text-xl font-bold text-white">
						Motor Insurance
					</h1>
					<div
						class="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1"
					>
						<img
							src="/assets/Logo/nexus.svg"
							alt="Company Logo"
							class="w-full h-full object-contain"
						/>
					</div>
				</div>
				<p class="text-blue-100 text-sm mt-2">
					Comprehensive vehicle protection
				</p>
			</div>

			<div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
				<nav class="flex items-center space-x-2 text-sm">
					<router-link
						class="text-primary hover:text-secondary transition-colors duration-200 flex items-center space-x-1"
						:to="{ name: 'Home' }"
					>
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
							></path>
						</svg>
						<span>Home</span>
					</router-link>

					<svg
						class="w-4 h-4 text-gray-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9 5l7 7-7 7"
						></path>
					</svg>

					<span class="text-gray-600 font-medium">Motor Private</span>

					<template
						v-if="currentStepLabel !== 'Motor Private Insurance'"
					>
						<svg
							class="w-4 h-4 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 5l7 7-7 7"
							></path>
						</svg>
						<span class="text-secondary font-semibold">{{
							currentStepLabel
						}}</span>
					</template>
				</nav>
			</div>

			<div class="flex-1 px-6 py-8 overflow-y-auto">
				<div class="space-y-6">
					<div
						v-for="(step, index) in steps"
						:key="step.name"
						class="relative"
					>
						<div
							v-if="index < steps.length - 1"
							class="absolute left-6 top-12 w-0.5 h-16 transition-colors duration-500"
							:class="
								isStepCompleted(step.name)
									? 'bg-secondary'
									: 'bg-gray-200'
							"
						></div>

						<div class="flex items-start space-x-4">
							<div class="relative flex-shrink-0">
								<div
									:class="[
										'flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 shadow-sm',
										isStepActive(step.name)
											? 'bg-primary text-white border-primary shadow-primary/20'
											: isStepCompleted(step.name)
												? 'bg-secondary text-white border-secondary shadow-secondary/20'
												: 'bg-white text-gray-500 border-gray-300',
									]"
								>
									<template
										v-if="
											isStepCompleted(step.name) &&
											!isStepActive(step.name)
										"
									>
										<svg
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2.5"
												d="M5 13l4 4L19 7"
											/>
										</svg>
									</template>
									<template v-else>
										<span class="text-sm font-bold">{{
											index + 1
										}}</span>
									</template>
								</div>

								<div
									v-if="isStepActive(step.name)"
									class="absolute inset-0 rounded-full border-2 border-primary/30 animate-pulse"
								></div>
							</div>

							<div class="flex-1 min-w-0 pb-8">
								<h3
									:class="[
										'text-base font-semibold transition-colors duration-300',
										isStepActive(step.name)
											? 'text-primary'
											: isStepCompleted(step.name)
												? 'text-secondary'
												: 'text-gray-700',
									]"
								>
									{{ step.label }}
								</h3>
								<p
									:class="[
										'text-sm mt-1 transition-colors duration-300 leading-relaxed',
										isStepActive(step.name)
											? 'text-primary/80'
											: isStepCompleted(step.name)
												? 'text-secondary/80'
												: 'text-gray-500',
									]"
								>
									{{ step.description }}
								</p>

								<div class="mt-3 flex items-center">
									<div
										:class="[
											'px-3 py-1 rounded-full text-xs font-medium',
											isStepActive(step.name)
												? 'bg-primary/10 text-primary border border-primary/20'
												: isStepCompleted(step.name)
													? 'bg-green-50 text-green-700 border border-green-200'
													: 'bg-gray-50 text-gray-500 border border-gray-200',
										]"
									>
										{{
											isStepActive(step.name)
												? 'In Progress'
												: isStepCompleted(step.name)
													? 'Completed'
													: 'Pending'
										}}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="px-6 py-4 bg-gray-50 border-t border-gray-100">
				<div class="text-center">
					<p class="text-xs text-gray-500">Need assistance?</p>
					<button
						@click="openHelp"
						class="text-sm text-primary hover:text-secondary font-medium mt-1 transition-colors duration-200"
					>
						Help
					</button>
				</div>
			</div>
		</aside>

		<main class="flex-1 flex flex-col min-h-0">
			<!-- Desktop Header -->
			<header
				class="hidden lg:flex bg-white border-b border-gray-200 sticky top-0 z-40"
			>
				<div class="w-full px-6 py-4">
					<div class="flex items-center justify-between">
						<div class="min-w-0 flex-1">
							<h2
								class="text-lg font-semibold text-secondary truncate"
							>
								{{ currentStepLabel }}
							</h2>
							<p class="text-sm text-gray-600">
								Complete your motor insurance application
							</p>
						</div>
						<div
							class="flex items-center space-x-4 flex-shrink-0 ml-6"
						>
							<button
								@click="openHelp"
								class="group relative bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-blue-500/25 transform transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 overflow-hidden animate-bounce hover:animate-none"
								style="animation: popUp 2s ease-in-out infinite"
								aria-label="Get help and support"
							>
								<div
									class="absolute -inset-0.5 bg-blue-400 rounded-lg blur opacity-60 animate-pulse"
								></div>
								<div
									class="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"
								></div>
								<div
									class="relative flex items-center space-x-2"
								>
									<svg
										class="w-4 h-4 transition-all duration-300 group-hover:scale-125 group-hover:rotate-12"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										style="
											animation: iconPop 2s ease-in-out
												infinite;
										"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										></path>
									</svg>
									<span class="text-sm font-bold relative"
										>Help</span
									>
								</div>
								<div
									class="absolute inset-0 rounded-lg border-2 border-blue-300 animate-ping opacity-75"
								></div>
								<div
									class="absolute inset-0 rounded-lg border-2 border-blue-200 animate-ping opacity-50"
									style="
										animation-delay: 0.5s;
										animation: ping 2s
											cubic-bezier(0, 0, 0.2, 1) infinite;
									"
								></div>
							</button>

							<div class="flex items-center space-x-3">
								<span
									class="text-sm text-gray-500 whitespace-nowrap"
									>Progress</span
								>
								<div
									class="w-32 h-2 bg-gray-200 rounded-full overflow-hidden"
								>
									<div
										class="h-full bg-secondary transition-all duration-500 rounded-full"
										:style="{
											width: progressPercentage + '%',
										}"
									></div>
								</div>
								<span
									class="text-sm font-medium text-secondary min-w-12 text-right"
									>{{ progressPercentage }}%</span
								>
							</div>
						</div>
					</div>
				</div>
			</header>

			<!-- Main Content Area -->
			<section class="flex-1 bg-white overflow-y-auto">
				<router-view class="h-full" />
			</section>

			<!-- Mobile Help Button -->
			<div class="lg:hidden fixed bottom-6 right-4 z-50">
				<button
					@click="openHelp"
					class="bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
					aria-label="Get help and support"
				>
					<svg
						class="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						></path>
					</svg>
				</button>
			</div>
		</main>
	</div>
</template>

<script>
import { FetchIntermediaries } from '../api/insurance';
import { mapActions } from 'vuex';
export default {
	name: 'MotorPrivate',
	data() {
		return {
			steps: [
				{
					name: 'BasicDetails',
					label: 'Get Quotation',
					shortLabel: 'Quote',
					description: 'Basic information and quote generation',
				},
				{
					name: 'CoverDetails',
					label: 'Risk Assessment',
					shortLabel: 'Risk',
					description: 'Vehicle details and coverage options',
				},
				{
					name: 'PaymentConfirmation',
					label: 'Payment & Confirmation',
					shortLabel: 'Payment',
					description: 'Review and complete your purchase',
				},
			],
		};
	},
	computed: {
		currentStepLabel() {
			const currentStep = this.steps.find((step) =>
				this.isStepActive(step.name),
			);
			return currentStep ? currentStep.label : 'Motor Private Insurance';
		},
		currentStepIndex() {
			return this.stepIndex(this.getCurrentStepName());
		},
		progressPercentage() {
			const currentStepIndex = this.stepIndex(this.getCurrentStepName());
			const totalSteps = this.steps.length;
			return Math.round(((currentStepIndex + 1) / totalSteps) * 100);
		},
	},
	methods: {
		...mapActions('help', ['setEntities']),

		isStepActive(stepName) {
			return this.$route.matched.some((r) => r.name === stepName);
		},
		isStepCompleted(stepName) {
			const currentStepIndex = this.stepIndex(this.getCurrentStepName());
			const stepIdx = this.stepIndex(stepName);
			return stepIdx < currentStepIndex;
		},
		stepIndex(stepName) {
			return this.steps.findIndex((step) => step.name === stepName);
		},
		getCurrentStepName() {
			const currentStep = this.steps.find((step) =>
				this.isStepActive(step.name),
			);
			return currentStep ? currentStep.name : 'BasicDetails';
		},
		async openHelp() {
			try {
				const res = await FetchIntermediaries();
				if (res.status) {
					this.setEntities(res.data);
					this.$router.push({ name: 'HelpMotor' });
				}
			} catch (error) {
				console.error('Error fetching intermediaries:', error);
				this.$router.push({ name: 'Home' });
			}
		},
	},

	created() {
		// Redirect to default route if invalid
		const validSteps = [
			'BasicDetails',
			'CoverDetails',
			'PaymentConfirmation',
		];
		if (!this.$route.matched.some((r) => validSteps.includes(r.name))) {
			this.$store.dispatch('insurance/motorPrivate/resetFlow');
			this.$router.replace({
				name: 'CoverTypeSelection',
				params: this.$route.params,
			});
		}
	},
	mounted() {
		// Sync root store if needed
		if (this.$store.state.insurance.currentStep !== 'motorPrivate') {
			this.$store.commit('insurance/currentStep', 'motorPrivate');
			this.$store.commit('insurance/selectedCategory', 'motorPrivate');
		}
	},
};
</script>

<style scoped>
aside::-webkit-scrollbar {
	width: 6px;
}

aside::-webkit-scrollbar-track {
	background: #f1f5f9;
}

aside::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

aside::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}

.transition-colors {
	transition-property: color, background-color, border-color;
	transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	transition-duration: 200ms;
}

.transition-all {
	transition-property: all;
	transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	transition-duration: 300ms;
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}

.animate-pulse {
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.sticky {
	position: sticky;
}

.fixed {
	position: fixed;
}

/* Mobile-specific improvements */
@media (max-width: 1023px) {
	/* Ensure proper viewport height on mobile */
	html,
	body {
		height: 100vh;
		overflow: hidden;
	}

	/* Main container setup for mobile scrolling */
	.min-h-screen {
		height: 100vh;
		overflow: hidden;
	}

	/* Mobile header stays sticky */
	.lg\\:hidden.sticky {
		position: sticky;
		top: 0;
		z-index: 50;
	}

	/* Content area scrolls properly */
	main {
		height: calc(100vh - 120px); /* Account for mobile header */
		overflow: hidden;
	}

	section {
		height: 100%;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}

	/* Better touch targets */
	button {
		min-height: 44px;
		min-width: 44px;
	}

	/* Safe area support */
	@supports (padding: max(0px)) {
		.lg\\:hidden.px-4 {
			padding-left: max(1rem, env(safe-area-inset-left));
			padding-right: max(1rem, env(safe-area-inset-right));
		}

		.fixed.bottom-6.right-4 {
			bottom: max(1.5rem, env(safe-area-inset-bottom));
			right: max(1rem, env(safe-area-inset-right));
		}
	}
}

@media (max-width: 640px) {
	.text-lg {
		font-size: 1rem;
		line-height: 1.5rem;
	}

	.text-xl {
		font-size: 1.125rem;
		line-height: 1.75rem;
	}

	/* Compact progress bar on small screens */
	.w-32 {
		width: 5rem;
	}
}

@media (max-width: 375px) {
	.space-x-3 > :not([hidden]) ~ :not([hidden]) {
		margin-left: 0.5rem;
	}
}

/* Focus styles for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid #669bbc;
	outline-offset: 2px;
}
@keyframes popUp {
	0%,
	100% {
		transform: scale(1) translateY(0);
	}
	25% {
		transform: scale(1.05) translateY(-2px);
		box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
	}
	50% {
		transform: scale(1.1) translateY(-4px);
		box-shadow: 0 12px 35px rgba(37, 99, 235, 0.5);
	}
	75% {
		transform: scale(1.05) translateY(-2px);
		box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
	}
}

@keyframes iconPop {
	0%,
	100% {
		transform: scale(1) rotate(0deg);
	}
	25% {
		transform: scale(1.1) rotate(5deg);
	}
	50% {
		transform: scale(1.2) rotate(-5deg);
	}
	75% {
		transform: scale(1.1) rotate(3deg);
	}
}

@keyframes ping {
	75%,
	100% {
		transform: scale(2);
		opacity: 0;
	}
}

@keyframes glowPulse {
	0%,
	100% {
		opacity: 0.6;
		filter: blur(4px);
	}
	50% {
		opacity: 0.9;
		filter: blur(6px);
	}
}

.animate-glow {
	animation: glowPulse 2s ease-in-out infinite;
}
</style>
